
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EnhancedTherapistSearch } from '@/components/search/EnhancedTherapistSearch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Users, MapPin, Sparkles, BrainCircuit, Globe, Clock, CreditCard, Star } from 'lucide-react';
import MasterSEOController from '@/components/seo/MasterSEOController';
import TherapistMatcher from '@/components/client/TherapistMatcher';
import { ExitIntentPopup } from '@/components/conversion/ExitIntentPopup';
import { MobileStickyCTA } from '@/components/conversion/MobileStickyCTA';

const Find: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [sessionFormat, setSessionFormat] = useState("any");
  
  const initialQuery = searchParams.get('q') || '';
  const initialLocation = searchParams.get('location') || '';

  return (
    <>
      <ExitIntentPopup />
      <MobileStickyCTA variant="find" />
      {/* Master SEO Controller - Gestión completa de SEO */}
      <MasterSEOController 
        pageType="find"
        currentLanguage="en"
        customMetaTags={{
          title: "Find Your Perfect Therapist",
          description: "Search our global directory of licensed psychologists, psychiatrists, and psychotherapists. Filter by specialty, location, language, price. Book online or in-person therapy sessions.",
          keywords: ['find therapist', 'psychologist near me', 'online therapy', 'mental health professional', 'anxiety therapist', 'depression counseling', 'therapy search', 'therapist directory']
        }}
        enabledFeatures={{
          multilingual: true,
          faqSchema: true,
          trustSignals: true
        }}
        ratings={{
          averageRating: 4.9,
          reviewCount: 2847
        }}
      />
      
      {/* Hero Section - Simplified */}
      <div className="bg-gradient-to-br from-primary/5 via-background to-accent/5 py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4">Find Your Therapist</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              5,000+ Verified Mental Health Professionals
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8">
              Search by specialty, language, location, or let AI match you with the perfect therapist
            </p>
            
            {/* Key Stats */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Badge variant="secondary" className="text-base py-2 px-4">
                <Users className="w-4 h-4 mr-2" />
                5,000+ Therapists
              </Badge>
              <Badge variant="secondary" className="text-base py-2 px-4">
                <Globe className="w-4 h-4 mr-2" />
                150+ Countries
              </Badge>
              <Badge variant="secondary" className="text-base py-2 px-4">
                <Star className="w-4 h-4 mr-2 fill-yellow-500 text-yellow-500" />
                4.8 Average Rating
              </Badge>
            </div>
          </div>
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="container mx-auto px-4 -mt-6 mb-12">
        <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-4">
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all border-2 hover:border-primary"
            onClick={() => document.getElementById('search')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <CardContent className="p-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
                <span className="font-bold text-lg">Available Now</span>
              </div>
              <p className="text-sm text-muted-foreground">237 therapists online</p>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all border-2 hover:border-primary"
            onClick={() => document.getElementById('search')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <CardContent className="p-6 text-center">
              <Globe className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="font-bold text-lg mb-1">Browse All</p>
              <p className="text-sm text-muted-foreground">Filter by specialty</p>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all border-2 hover:border-primary"
            onClick={() => {
              const tabs = document.querySelector('[role="tablist"]');
              const aiTab = tabs?.querySelector('[value="ai-match"]') as HTMLElement;
              aiTab?.click();
            }}
          >
            <CardContent className="p-6 text-center">
              <Sparkles className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="font-bold text-lg mb-1">AI Matching</p>
              <p className="text-sm text-muted-foreground">Get personalized match</p>
            </CardContent>
          </Card>
        </div>
      </div>
        
        <div id="search" className="container mx-auto px-4">
        <Tabs defaultValue="search" className="max-w-5xl mx-auto">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="search" className="text-base py-3">
              <Search className="h-4 w-4 mr-2" />
              <span className="whitespace-nowrap">Search Directory</span>
            </TabsTrigger>
            <TabsTrigger value="ai-match" className="text-base py-3">
              <BrainCircuit className="h-4 w-4 mr-2" />
              <span className="whitespace-nowrap">AI Matching</span>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="search" className="space-y-8">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center text-2xl">
                  <Search className="h-5 w-5 mr-3" />
                  Advanced Search
                </CardTitle>
                <CardDescription className="text-base">
                  Find therapists by location, specialty, language, price range, and more
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <EnhancedTherapistSearch />
                
                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="bg-muted/50">
                    <CardContent className="pt-6 flex flex-col items-center text-center">
                      <div className="mb-4 bg-primary/10 p-3 rounded-full">
                        <MapPin className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-medium mb-3">Local Therapists</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Find therapists near you for in-person sessions
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-muted/50">
                    <CardContent className="pt-6 flex flex-col items-center text-center">
                      <div className="mb-4 bg-primary/10 p-3 rounded-full">
                        <Globe className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-medium mb-3">Multilingual Therapy</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Find therapists who speak your preferred language
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-muted/50">
                    <CardContent className="pt-6 flex flex-col items-center text-center">
                      <div className="mb-4 bg-primary/10 p-3 rounded-full">
                        <Clock className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-medium mb-3">Immediate Availability</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Connect with therapists available for instant sessions
                      </p>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="bg-muted/50">
                    <CardContent className="pt-6 flex flex-col items-center text-center">
                      <div className="mb-4 bg-primary/10 p-3 rounded-full">
                        <Users className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-medium mb-3">Specialty Matching</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Find therapists who specialize in your specific needs
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-muted/50">
                    <CardContent className="pt-6 flex flex-col items-center text-center">
                      <div className="mb-4 bg-primary/10 p-3 rounded-full">
                        <CreditCard className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-medium mb-3">Price Range Options</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Find therapists that fit your budget and payment options
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-muted/50">
                    <CardContent className="pt-6 flex flex-col items-center text-center">
                      <div className="mb-4 bg-primary/10 p-3 rounded-full">
                        <Sparkles className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-medium mb-3">Online Therapy</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Connect with therapists virtually from anywhere
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="ai-match">
            <TherapistMatcher />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
};

export default Find;
